import React from 'react';
import { Drawer, Box, Button, Typography, TextField, IconButton, Tooltip, Alert, Stack, Divider, InputAdornment, Chip } from '@mui/material';
import CalendarToday from '@mui/icons-material/CalendarToday';
import { createMyTask } from '../../services/localTasksService';
import { runNoteAiTasks, AiTaskItem } from '../../services/aiJobsService';

type Item = { id: string; title: string; dueYmd: string | null; editing?: boolean; collapsed?: boolean };

function toIsoFromYmd(ymd: string | null): string | null {
  if (!ymd) return null;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

export default function NoteAiTasksDialog({ open, onClose, noteId, noteTitle }: { open: boolean; onClose: () => void; noteId: string; noteTitle?: string }) {
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<Item[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const dateInputsRef = React.useRef<Record<string, HTMLInputElement | null>>({});

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setItems([]);
    setLoading(true);
    (async () => {
      try {
        const { items: list } = await runNoteAiTasks(noteId);
        const mapped: Item[] = (Array.isArray(list) ? list : []).map((t: AiTaskItem, idx: number) => ({
          id: `${Date.now()}-${idx}`,
          title: String(t.title || '').trim(),
          dueYmd: t.due_date || null,
          editing: false,
          collapsed: false
        })).filter(it => it.title);
        setItems(mapped);
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Błąd generowania zadań');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, noteId]);

  const addToMyTasks = async () => {
    for (const it of items) {
      const title = String(it.title || '').trim();
      if (!title) continue;
      const dueIso = toIsoFromYmd(it.dueYmd);
      await createMyTask({ title, due: dueIso });
    }
    onClose();
  };

  const onDelete = (id: string) => setItems(prev => prev.filter(i => i.id !== id));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 560, maxWidth: '100vw', borderRadius: 0 } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Zadania AI {noteTitle ? `– ${noteTitle}` : ''}
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 3 }} />
        <Box sx={{ p: 4, pt: 2, flex: 1, overflowY: 'auto' }}>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {loading ? (
            <Typography>Generowanie…</Typography>
          ) : (
            <Stack spacing={1.25}>
              {items.map((it) => (
                <Box key={it.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 0.5 }}>
                  <Box sx={{ flex: 1 }}>
                    {it.editing ? (
                      <TextField
                        value={it.title}
                        onChange={(e)=>setItems(prev => prev.map(p => p.id === it.id ? { ...p, title: e.target.value } : p))}
                        placeholder="Tytuł zadania"
                        fullWidth
                        size="small"
                        multiline
                        minRows={1}
                      />
                    ) : (
                      <Typography sx={{ fontSize: 14, color: '#2D2D32', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {it.title || '(bez nazwy)'}
                      </Typography>
                    )}
                    {/* Pasek daty w stylu 'Moje zadania' */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                      {it.dueYmd ? (
                        <Box sx={{ fontSize: 12, px: 1, py: 0.25, borderRadius: '6px', bgcolor: '#FFF5CC', color: '#020242' }}>
                          {it.dueYmd}
                        </Box>
                      ) : (
                        <>
                          <Chip
                            label="Dzisiaj"
                            size="small"
                            onClick={()=>{
                              const today = new Date();
                              const y = today.getFullYear();
                              const m = String(today.getMonth()+1).padStart(2,'0');
                              const d = String(today.getDate()).padStart(2,'0');
                              const ymd = `${y}-${m}-${d}`;
                              setItems(prev => prev.map(p => p.id === it.id ? { ...p, dueYmd: ymd } : p));
                            }}
                            sx={{ bgcolor: '#FFF5CC', color: '#020242', height: 22, borderRadius: '6px', '& .MuiChip-label': { px: 1 } }}
                          />
                          <Chip
                            label="Jutro"
                            size="small"
                            onClick={()=>{
                              const t = new Date(); t.setDate(t.getDate()+1);
                              const y = t.getFullYear();
                              const m = String(t.getMonth()+1).padStart(2,'0');
                              const d = String(t.getDate()).padStart(2,'0');
                              const ymd = `${y}-${m}-${d}`;
                              setItems(prev => prev.map(p => p.id === it.id ? { ...p, dueYmd: ymd } : p));
                            }}
                            sx={{ bgcolor: '#EEF2FF', color: '#1F1FB0', height: 22, borderRadius: '6px', '& .MuiChip-label': { px: 1 } }}
                          />
                        </>
                      )}
                      <IconButton size="small" onClick={()=>{
                        const input = dateInputsRef.current[it.id];
                        if (input) {
                          try { (input as any).showPicker?.(); } catch {}
                          try { input.click(); } catch {}
                        }
                      }}>
                        <CalendarToday fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                      <input
                        type="date"
                        ref={(el)=>{ dateInputsRef.current[it.id] = el; }}
                        style={{ display: 'none' }}
                        value={it.dueYmd || ''}
                        onChange={(e)=>setItems(prev => prev.map(p => p.id === it.id ? { ...p, dueYmd: e.target.value || null } : p))}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.25 }}>
                    <Tooltip title={it.editing ? 'Zapisz' : 'Edytuj'}>
                      <IconButton size="small" onClick={() => setItems(prev => prev.map(p => p.id === it.id ? { ...p, editing: !p.editing } : p))}>
                        <Box component="img" src="/assets/icons/edit2.svg" alt="edit" sx={{ width: 18, height: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Usuń">
                      <IconButton size="small" onClick={()=>onDelete(it.id)}>
                        <Box component="img" src="/assets/icons/delete2.svg" alt="delete" sx={{ width: 18, height: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
              {items.length === 0 && !loading && !error ? (
                <Typography color="text.secondary">Brak propozycji zadań.</Typography>
              ) : null}
            </Stack>
          )}
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ mr: 1 }}>Anuluj</Button>
          <Button onClick={addToMyTasks} variant="contained" disabled={loading || items.length === 0}>Dodaj do moich zadań</Button>
        </Box>
      </Box>
    </Drawer>
  );
}


